import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "../lib/api";
import Layout from "../components/Layout";
import { useAuth } from "../contexts/AuthContext";
import { PenLine, Coins } from "lucide-react";
import AccessModeSelector, { packAccess } from "../components/AccessModeSelector";

export default function NovelleEditor() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [title, setTitle] = useState("");
  const [teaser, setTeaser] = useState("");
  const [body, setBody] = useState("");
  const [themesStr, setThemesStr] = useState("");
  const [warningsStr, setWarningsStr] = useState("");
  const [tokenCost, setTokenCost] = useState(0);
  const [access, setAccess] = useState({ mode: "public" });
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);

  if (user === false || user === null) {
    return (
      <Layout>
        <div className="font-meta text-[var(--cream-soft)]">Logg inn for å publisere.</div>
      </Layout>
    );
  }

  const wordCount = body.trim().split(/\s+/).filter(Boolean).length;

  const submit = async () => {
    setError("");
    if (title.trim().length < 3) return setError("Tittel må være minst 3 tegn.");
    if (teaser.trim().length < 10) return setError("Sammendrag må være minst 10 tegn.");
    if (body.trim().length < 20) return setError("Selve novellen må være minst 20 tegn.");

    setSubmitting(true);
    try {
      const packed = packAccess(access);
      const r = await api.post("/noveller", {
        title: title.trim(),
        teaser: teaser.trim(),
        body,
        themes: themesStr.split(",").map((s) => s.trim()).filter(Boolean),
        content_warnings: warningsStr.split(",").map((s) => s.trim()).filter(Boolean),
        token_cost: packed.price_tokens || 0,
        visibility: packed.visibility,
        access_friends_free: packed.access_friends_free,
        access_vip_free: packed.access_vip_free,
      });
      navigate(`/noveller/${r.data.id}`);
    } catch (e) {
      setError(formatApiError(e));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Layout withSidebar={false}>
      <div className="max-w-3xl mx-auto">
        <div className="font-meta mb-3">Bibliotek / Skriv</div>
        <h1 className="font-fraunces italic text-5xl text-[var(--cream)] leading-tight mb-3">
          Skriv en novelle.
        </h1>
        <p className="font-manrope text-sm text-[var(--cream-soft)] max-w-xl leading-relaxed mb-10">
          Sammendraget er alltid synlig gratis. Selve novellen kan koste tokens å låse opp – 90 % går
          til deg som forfatter. Du kan redigere senere.
        </p>

        <div className="editorial-card p-8 space-y-6" data-testid="novelle-editor">
          <div>
            <div className="font-meta mb-2">Tittel</div>
            <input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              placeholder="Gi novellen en sterk tittel"
              className="input-editorial font-fraunces italic text-2xl"
              data-testid="editor-title"
            />
          </div>

          <div>
            <div className="font-meta mb-2">Sammendrag (alltid gratis å se)</div>
            <textarea
              value={teaser}
              onChange={(e) => setTeaser(e.target.value)}
              maxLength={600}
              rows={3}
              placeholder="Et lite drag som lokker leseren inn…"
              className="input-editorial resize-y font-fraunces"
              data-testid="editor-teaser"
            />
            <div className="font-meta mt-1 text-[10px] text-right">{teaser.length} / 600</div>
          </div>

          <div>
            <div className="font-meta mb-2">Selve novellen</div>
            <textarea
              value={body}
              onChange={(e) => setBody(e.target.value)}
              maxLength={80000}
              rows={20}
              placeholder="Skriv her. Linjeskift bevares."
              className="input-editorial resize-y font-fraunces text-base leading-[1.8]"
              data-testid="editor-body"
            />
            <div className="font-meta mt-1 text-[10px] text-right">
              {wordCount} ord · {body.length} / 80 000 tegn
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div>
              <div className="font-meta mb-2">
                Tema{" "}
                <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)]">
                  (kommaseparert)
                </span>
              </div>
              <input
                value={themesStr}
                onChange={(e) => setThemesStr(e.target.value)}
                placeholder="første gang, hotell, bdsm"
                className="input-editorial"
                data-testid="editor-themes"
              />
            </div>
            <div>
              <div className="font-meta mb-2">
                Innholdsadvarsel{" "}
                <span className="normal-case tracking-normal text-[10px] text-[var(--cream-faint)]">
                  (valgfritt)
                </span>
              </div>
              <input
                value={warningsStr}
                onChange={(e) => setWarningsStr(e.target.value)}
                placeholder="vold, dominans"
                className="input-editorial"
              />
            </div>
            <div className="sm:col-span-2">
              <AccessModeSelector value={access} onChange={setAccess} />
              <div className="font-meta mt-3 text-[10px] text-[var(--cream-faint)]">
                ✓ Noveller krever IKKE selger-verifisering (i motsetning til bilder/video).
              </div>
            </div>
          </div>

          {error && (
            <div className="font-medium text-sm text-[var(--cream)] border-l-2 border-[var(--magenta)] pl-3" data-testid="editor-error">
              {error}
            </div>
          )}

          <div className="flex justify-end pt-4 border-t border-white/10">
            <button
              onClick={submit}
              disabled={submitting}
              className="btn-magenta inline-flex items-center"
              data-testid="editor-submit"
            >
              <PenLine className="h-3.5 w-3.5 mr-2" />
              {submitting ? "Publiserer…" : "Publiser novelle"}
            </button>
          </div>
        </div>
      </div>
    </Layout>
  );
}
